import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import { getDependentTeamsForSystem } from "helpers/shared.helpers";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Pressable, StyleSheet, Switch, Text, View } from "react-native";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import { OrganizationContext } from "webContext/Organization.context";
import {
  toggleEntitySelection,
  toggleSelectAllEntities,
} from "../../../../src/components/screens/create-alert/utils/createAlert.utils";

const classes = StyleSheet.create({
  root: {
    gap: 10,
    width: 340,
  },
  labelText: {
    fontSize: 18,
    color: theme.color.backgroundText,
  },
  smallTeamText: {
    fontSize: 14,
    paddingLeft: 22,
    paddingVertical: 2,
    color: theme.color.darkGrey,
  },
  labelTextBold: {
    fontWeight: theme.font.weight.semibold,
  },
  smallText: {
    fontSize: 14,
    paddingHorizontal: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
  },
  rowEndContainer: {
    flexDirection: "row",
    gap: 30,
    alignItems: "center",
  },
  switchContainer: {
    flexDirection: "column",
    alignItems: "center",
  },
  labelOnlineOfflineText: {
    fontWeight: theme.font.weight.medium,
    paddingTop: 4,
    fontSize: 12,
  },
});

export default function SystemSelectionModal(props) {
  const {
    open,
    onClose,
    selectedSystemIds,
    setSelectedSystemIds,
    systemIsOnlineMap,
    onChangeSystemStatus,
    recipients = [],
    setRecipients,
    selectedTeamIds = [],
    setSelectedTeamIds,
    selectedLocationIds = [],
    setSelectedLocationIds,
  } = props;

  const {
    organizationColor,
    organizationSystems,
    teamList,
    locationList,
    personsByTeam,
    personsByLocation,
  } = useContext(OrganizationContext);

  const [searchText, setSearchText] = useState("");
  const [newSelectedSystemIds, setNewSelectedSystemIds] = useState(
    new Set(selectedSystemIds),
  );
  const [localTeamIds, setLocalTeamIds] = useState(new Set());
  const [localLocationIds, setLocalLocationIds] = useState(new Set());
  const [localPersonIds, setLocalPersonIds] = useState(new Set());

  useEffect(() => {
    setNewSelectedSystemIds(new Set(selectedSystemIds));
    const newLocalTeamIds = new Set(selectedTeamIds);
    const newLocalLocationIds = new Set(selectedLocationIds);
    const newLocalPersonIds = new Set(recipients);
    setLocalTeamIds(newLocalTeamIds);
    setLocalLocationIds(newLocalLocationIds);
    setLocalPersonIds(newLocalPersonIds);
  }, [selectedSystemIds, selectedTeamIds, selectedLocationIds, recipients]);

  const onSave = useCallback(() => {
    setSelectedSystemIds([...newSelectedSystemIds]);
    if (setSelectedTeamIds) {
      setSelectedTeamIds([...localTeamIds]);
    }
    if (setSelectedLocationIds) {
      setSelectedLocationIds([...localLocationIds]);
    }
    if (setRecipients) {
      setRecipients([...localPersonIds]);
    }
    onClose([...newSelectedSystemIds]);
  }, [
    newSelectedSystemIds,
    localTeamIds,
    localLocationIds,
    localPersonIds,
    setSelectedSystemIds,
    setSelectedTeamIds,
    setSelectedLocationIds,
    setRecipients,
    onClose,
  ]);

  const toggleSystem = useCallback(
    (systemId) => {
      const tempSet = new Set(newSelectedSystemIds);
      const isSelecting = !tempSet.has(systemId);

      if (tempSet.has(systemId)) {
        tempSet.delete(systemId);
      } else {
        tempSet.add(systemId);
      }

      onChangeSystemStatus(
        systemId,
        systemIsOnlineMap[systemId].current,
        tempSet.has(systemId),
      );

      // Handle dependent teams and locations
      if (isSelecting) {
        const foundSystem = organizationSystems.find(
          (system) => system.organizationSystemId === systemId,
        );
        const dependentData = getDependentTeamsForSystem(
          foundSystem,
          teamList,
          locationList,
        );
        const { teamIds, locationIds, hasOrganizationDependency } =
          dependentData;

        let updatedTeamIds = new Set(localTeamIds);
        let updatedLocationIds = new Set(localLocationIds);
        let updatedPersonIds = new Set(localPersonIds);

        if (hasOrganizationDependency) {
          // Select all teams and locations
          const { updatedEntities: allTeams, updatedPersons: teamPersons } =
            toggleSelectAllEntities(
              false, // always select all
              teamList,
              personsByTeam,
              "teamId",
            );

          const {
            updatedEntities: allLocations,
            updatedPersons: locationPersons,
          } = toggleSelectAllEntities(
            false, // always select all
            locationList,
            personsByLocation,
            "locationId",
          );

          updatedTeamIds = allTeams;
          updatedLocationIds = allLocations;
          updatedPersonIds = new Set([...teamPersons, ...locationPersons]);
        } else {
          // Select specific teams and locations
          teamIds.forEach((teamId) => {
            if (!updatedTeamIds.has(teamId)) {
              const { updatedEntities, updatedPersons } = toggleEntitySelection(
                updatedTeamIds,
                updatedPersonIds,
                personsByTeam,
                teamId,
              );
              updatedTeamIds = updatedEntities;
              updatedPersonIds = updatedPersons;
            }
          });

          locationIds.forEach((locationId) => {
            if (!updatedLocationIds.has(locationId)) {
              const { updatedEntities, updatedPersons } = toggleEntitySelection(
                updatedLocationIds,
                updatedPersonIds,
                personsByLocation,
                locationId,
              );
              updatedLocationIds = updatedEntities;
              updatedPersonIds = updatedPersons;
            }
          });
        }

        setLocalTeamIds(updatedTeamIds);
        setLocalLocationIds(updatedLocationIds);
        setLocalPersonIds(updatedPersonIds);

        // Immediately propagate changes to parent component
        if (setSelectedTeamIds) {
          setSelectedTeamIds([...updatedTeamIds]);
        }
        if (setSelectedLocationIds) {
          setSelectedLocationIds([...updatedLocationIds]);
        }
        if (setRecipients) {
          setRecipients([...updatedPersonIds]);
        }
      } else {
        // Deselecting system - remove its dependent teams and locations
        const foundSystem = organizationSystems.find(
          (system) => system.organizationSystemId === systemId,
        );
        const dependentData = getDependentTeamsForSystem(
          foundSystem,
          teamList,
          locationList,
        );
        const { teamIds, locationIds, hasOrganizationDependency } =
          dependentData;

        let updatedTeamIds = new Set(localTeamIds);
        let updatedLocationIds = new Set(localLocationIds);
        let updatedPersonIds = new Set(localPersonIds);

        if (hasOrganizationDependency) {
          // Check if any other selected systems have organization dependencies
          const otherOrganizationSystems = [...tempSet].some(
            (otherSystemId) => {
              const otherFoundSystem = organizationSystems.find(
                (system) => system.organizationSystemId === otherSystemId,
              );
              if (!otherFoundSystem || otherSystemId === systemId) {
                return false;
              }
              const otherDependentData = getDependentTeamsForSystem(
                otherFoundSystem,
                teamList,
                locationList,
              );
              return otherDependentData.hasOrganizationDependency;
            },
          );

          if (!otherOrganizationSystems) {
            // No other systems have organization dependencies, clear all
            updatedTeamIds = new Set();
            updatedLocationIds = new Set();
            updatedPersonIds = new Set();
          }
        } else {
          // Remove specific teams and locations if they're not needed by other systems
          teamIds.forEach((teamId) => {
            // Check if this team is needed by any other selected systems
            const isNeededByOtherSystems = [...tempSet].some(
              (otherSystemId) => {
                const otherFoundSystem = organizationSystems.find(
                  (system) => system.organizationSystemId === otherSystemId,
                );
                if (!otherFoundSystem || otherSystemId === systemId) {
                  return false;
                }
                const otherDependentData = getDependentTeamsForSystem(
                  otherFoundSystem,
                  teamList,
                  locationList,
                );
                return (
                  otherDependentData.teamIds.includes(teamId) ||
                  otherDependentData.hasOrganizationDependency
                );
              },
            );

            if (!isNeededByOtherSystems && updatedTeamIds.has(teamId)) {
              const { updatedEntities, updatedPersons } = toggleEntitySelection(
                updatedTeamIds,
                updatedPersonIds,
                personsByTeam,
                teamId,
              );
              updatedTeamIds = updatedEntities;
              updatedPersonIds = updatedPersons;
            }
          });

          locationIds.forEach((locationId) => {
            // Check if this location is needed by any other selected systems
            const isNeededByOtherSystems = [...tempSet].some(
              (otherSystemId) => {
                const otherFoundSystem = organizationSystems.find(
                  (system) => system.organizationSystemId === otherSystemId,
                );
                if (!otherFoundSystem || otherSystemId === systemId) {
                  return false;
                }
                const otherDependentData = getDependentTeamsForSystem(
                  otherFoundSystem,
                  teamList,
                  locationList,
                );
                return (
                  otherDependentData.locationIds.includes(locationId) ||
                  otherDependentData.hasOrganizationDependency
                );
              },
            );

            if (!isNeededByOtherSystems && updatedLocationIds.has(locationId)) {
              const { updatedEntities, updatedPersons } = toggleEntitySelection(
                updatedLocationIds,
                updatedPersonIds,
                personsByLocation,
                locationId,
              );
              updatedLocationIds = updatedEntities;
              updatedPersonIds = updatedPersons;
            }
          });
        }

        setLocalTeamIds(updatedTeamIds);
        setLocalLocationIds(updatedLocationIds);
        setLocalPersonIds(updatedPersonIds);

        // Immediately propagate changes to parent component
        if (setSelectedTeamIds) {
          setSelectedTeamIds([...updatedTeamIds]);
        }
        if (setSelectedLocationIds) {
          setSelectedLocationIds([...updatedLocationIds]);
        }
        if (setRecipients) {
          setRecipients([...updatedPersonIds]);
        }
      }

      setSelectedSystemIds([...tempSet]);
      setNewSelectedSystemIds(tempSet);
    },
    [
      newSelectedSystemIds,
      onChangeSystemStatus,
      systemIsOnlineMap,
      setSelectedSystemIds,
      organizationSystems,
      teamList,
      locationList,
      localTeamIds,
      localLocationIds,
      localPersonIds,
      personsByTeam,
      personsByLocation,
      setSelectedTeamIds,
      setSelectedLocationIds,
      setRecipients,
    ],
  );

  const filteredSystems = organizationSystems.filter((system) =>
    system.title.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <CustomModal open={open} onClose={onSave} title="Select Platforms" scroll>
      <View style={classes.root}>
        <SearchBar value={searchText} onChangeText={setSearchText} />
        <View style={classes.row}>
          <Text style={[classes.labelText, classes.labelTextBold]}>
            All Platforms
          </Text>
          <PressableContainer
            onPress={() => {
              const all = organizationSystems.map(
                (system) => system.organizationSystemId,
              );
              const allSelected = all.every((id) =>
                newSelectedSystemIds.has(id),
              );

              if (allSelected) {
                setNewSelectedSystemIds(new Set());
                setLocalTeamIds(new Set());
                setLocalLocationIds(new Set());
                setLocalPersonIds(new Set());
                all.forEach((id) =>
                  onChangeSystemStatus(
                    id,
                    systemIsOnlineMap[id]?.current,
                    false,
                  ),
                );
                if (setSelectedTeamIds) {
                  setSelectedTeamIds([]);
                }
                if (setSelectedLocationIds) {
                  setSelectedLocationIds([]);
                }
                if (setRecipients) {
                  setRecipients([]);
                }
              } else {
                setNewSelectedSystemIds(new Set(all));

                // Select all teams and locations for all systems
                const {
                  updatedEntities: allTeams,
                  updatedPersons: teamPersons,
                } = toggleSelectAllEntities(
                  false, // always select all
                  teamList,
                  personsByTeam,
                  "teamId",
                );

                const {
                  updatedEntities: allLocations,
                  updatedPersons: locationPersons,
                } = toggleSelectAllEntities(
                  false, // always select all
                  locationList,
                  personsByLocation,
                  "locationId",
                );

                setLocalTeamIds(allTeams);
                setLocalLocationIds(allLocations);
                setLocalPersonIds(
                  new Set([...teamPersons, ...locationPersons]),
                );

                all.forEach((id) =>
                  onChangeSystemStatus(
                    id,
                    systemIsOnlineMap[id]?.current,
                    true,
                  ),
                );
                if (setSelectedTeamIds) {
                  setSelectedTeamIds([...allTeams]);
                }
                if (setSelectedLocationIds) {
                  setSelectedLocationIds([...allLocations]);
                }
                if (setRecipients) {
                  setRecipients([
                    ...new Set([...teamPersons, ...locationPersons]),
                  ]);
                }
              }
            }}
          >
            <Icon
              name={
                filteredSystems.every((system) =>
                  newSelectedSystemIds.has(system.organizationSystemId),
                )
                  ? "check"
                  : "checkbox-blank-outline"
              }
              size={25}
              color={organizationColor}
            />
          </PressableContainer>
        </View>

        {filteredSystems.map((system) => {
          const dependentData = getDependentTeamsForSystem(
            system,
            teamList,
            locationList,
          );
          const {
            teams: dependentTeams,
            locations: dependentLocations,
            hasOrganizationDependency,
          } = dependentData;

          return (
            <View key={system.organizationSystemId}>
              <View style={classes.row}>
                <Text style={classes.labelText}>
                  {system.title}
                  {!!system.isOnline !==
                    systemIsOnlineMap[system.organizationSystemId]?.current &&
                    "*"}
                </Text>

                <View style={classes.rowEndContainer}>
                  <View style={classes.switchContainer}>
                    <Switch
                      trackColor={{
                        false: theme.color.grey,
                        true: theme.color.grey,
                      }}
                      thumbColor={
                        systemIsOnlineMap[system.organizationSystemId]?.current
                          ? theme.color.green
                          : theme.color.red
                      }
                      value={
                        systemIsOnlineMap[system.organizationSystemId]?.current
                      }
                      onValueChange={(value) =>
                        onChangeSystemStatus(
                          system.organizationSystemId,
                          value,
                          false,
                        )
                      }
                    />
                    <Text
                      style={[
                        classes.labelOnlineOfflineText,
                        {
                          color: systemIsOnlineMap[system.organizationSystemId]
                            ?.current
                            ? theme.color.green
                            : theme.color.red,
                        },
                      ]}
                    >
                      {systemIsOnlineMap[system.organizationSystemId]?.current
                        ? "Online"
                        : "Offline"}
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => toggleSystem(system.organizationSystemId)}
                    hitSlop={10}
                  >
                    <Icon
                      name={
                        newSelectedSystemIds.has(system.organizationSystemId)
                          ? "check"
                          : "checkbox-blank-outline"
                      }
                      size={25}
                      color={organizationColor}
                    />
                  </Pressable>
                </View>
              </View>

              {(dependentTeams.length > 0 ||
                dependentLocations.length > 0 ||
                hasOrganizationDependency) && (
                <View>
                  {hasOrganizationDependency && (
                    <Text style={classes.smallTeamText}>
                      Entire Organization
                    </Text>
                  )}
                  {dependentTeams.map((team) => (
                    <Text key={team.teamId} style={classes.smallTeamText}>
                      Team: {team.name}
                    </Text>
                  ))}
                  {dependentLocations.map((location) => (
                    <Text
                      key={location.locationId}
                      style={classes.smallTeamText}
                    >
                      Location: {location.name}
                    </Text>
                  ))}
                </View>
              )}
            </View>
          );
        })}

        <Text style={[classes.labelText, classes.smallText]}>
          * denotes modified platforms
        </Text>
      </View>
    </CustomModal>
  );
}
